import { useMemo } from 'react';
import { HeapRecordingDTO, HeapRleMaskDTO } from '../generated';
import {
  DEFAULT_HEAP_MASK_OPACITY,
  HeapMaskBitmap,
  HeapMaskEntry,
  HeapMaskOverlay,
  HeapMaskPreferences,
  resolveHeapMaskColor,
} from '../../components/Heap/heapMasks';

export interface UseHeapMasksParams {
  masks: HeapRecordingDTO['masks'];
  preferences: HeapMaskPreferences;
}

export interface UseHeapMasksResult {
  entries: HeapMaskEntry[];
  overlays: HeapMaskOverlay[];
}

export function useHeapMasks({ masks, preferences }: UseHeapMasksParams): UseHeapMasksResult {
  const { visibility, opacity } = preferences;

  const descriptors = useMemo(
    () =>
      Object.keys(masks ?? {})
        .sort()
        .map((id) => ({
          id,
          labelKey: `heap.masks.${id}`,
          color: resolveHeapMaskColor(id),
        })),
    [masks]
  );

  // separate from descriptors to avoid re-decoding RLE when opacity changes
  const entries = useMemo(
    () =>
      descriptors.map((descriptor) => ({
        ...descriptor,
        opacity: opacity[descriptor.id] ?? DEFAULT_HEAP_MASK_OPACITY,
        enabled: visibility[descriptor.id] === true,
      })),
    [descriptors, visibility, opacity]
  );

  const enabledMasks = useMemo(
    () => descriptors.filter(({ id }) => visibility[id] === true),
    [descriptors, visibility]
  );

  const bitmaps = useMemo(() => {
    const decoded = new Map<string, HeapMaskBitmap>();
    for (const { id } of enabledMasks) {
      try {
        decoded.set(id, decodeHeapRleMask(masks![id]));
      } catch (error) {
        console.error('Failed to decode segmentation mask', error);
      }
    }
    return decoded;
  }, [masks, enabledMasks]);

  const overlays = useMemo(
    () =>
      entries.flatMap((entry) => {
        const bitmap = bitmaps.get(entry.id);
        return bitmap ? [{ id: entry.id, bitmap, color: entry.color, opacity: entry.opacity }] : [];
      }),
    [entries, bitmaps]
  );

  return { entries, overlays };
}

export function decodeHeapRleMask({ size, counts }: HeapRleMaskDTO): HeapMaskBitmap {
  const [height, width] = size;
  const pixels = new Uint8Array(width * height);

  let position = 0;
  let foreground = false;
  for (const count of counts) {
    if (foreground) {
      for (let offset = 0; offset < count; offset++) {
        const flattened = position + offset;
        pixels[(flattened % height) * width + Math.floor(flattened / height)] = 1;
      }
    }
    position += count;
    foreground = !foreground;
  }

  return { width, height, pixels };
}
